import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InquiryForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    check_in: "",
    check_out: "",
    guests: 4,
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        guests: Number(form.guests),
      });
      toast.success("Inquiry sent! We'll reply within 24 hours.");
      setForm({ name: "", email: "", check_in: "", check_out: "", guests: 4, message: "", website: "" });
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many requests. Please try again in a bit, or email help@universalhousetx.com");
      } else {
        toast.error("Something went wrong. Please try again or email help@universalhousetx.com");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border-4 border-slate-900 rounded-2xl px-4 py-3 font-quicksand font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-brand-yellow";

  return (
    <section id="inquiry" data-testid="inquiry-section" className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-5xl mx-auto">
        <div className="relative bg-brand-blue border-4 border-slate-900 rounded-[2rem] p-6 sm:p-10 lg:p-14 neo-shadow-lg overflow-hidden">
          {/* deco */}
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-brand-yellow border-4 border-slate-900 rounded-full neo-shadow-sm grid place-items-center rotate-12">
            <Sparkles size={32} className="text-slate-900" />
          </div>
          <div className="absolute -bottom-8 -left-8 w-28 h-28 bg-brand-pink border-4 border-slate-900 rounded-3xl neo-shadow-sm rotate-12" />

          <div className="relative">
            <span className="inline-block bg-white text-slate-900 font-fredoka font-bold rounded-full px-4 py-1.5 border-4 border-slate-900 neo-shadow-sm rotate-[-2deg]">
              QUESTIONS? CHECK DATES?
            </span>
            <h2 className="font-fredoka font-bold text-4xl sm:text-5xl text-white mt-5">
              Ask away — we reply fast.
            </h2>
            <p className="text-white/90 mt-3 text-lg max-w-2xl">
              Send us a note about your trip and we&apos;ll get back within 24 hours with availability, details, and tips for the perfect family stay.
            </p>

            <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-4">
              {/* Honeypot — hidden from humans, bots fill it */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={update("website")}
                aria-hidden="true"
                className="absolute opacity-0 pointer-events-none w-0 h-0 -z-10"
                style={{ position: "absolute", left: "-9999px" }}
              />
              <input
                data-testid="inquiry-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                className={inputCls}
                required
              />
              <input
                data-testid="inquiry-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@email.com"
                className={inputCls}
                required
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white font-fredoka font-semibold text-sm mb-1.5 ml-1">Check-in</label>
                  <input data-testid="inquiry-checkin" type="date" value={form.check_in} onChange={update("check_in")} className={inputCls} />
                </div>
                <div>
                  <label className="block text-white font-fredoka font-semibold text-sm mb-1.5 ml-1">Check-out</label>
                  <input data-testid="inquiry-checkout" type="date" value={form.check_out} onChange={update("check_out")} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-white font-fredoka font-semibold text-sm mb-1.5 ml-1">Guests</label>
                <input
                  data-testid="inquiry-guests"
                  type="number"
                  min={1}
                  max={12}
                  value={form.guests}
                  onChange={update("guests")}
                  className={inputCls}
                />
              </div>
              <textarea
                data-testid="inquiry-message"
                value={form.message}
                onChange={update("message")}
                rows={4}
                placeholder="Tell us about your family & trip..."
                className={`${inputCls} sm:col-span-2 resize-none`}
              />
              <button
                data-testid="inquiry-submit"
                type="submit"
                disabled={submitting}
                className="sm:col-span-2 font-fredoka font-bold text-xl bg-brand-yellow text-slate-900 border-4 border-slate-900 rounded-full px-8 py-4 neo-shadow neo-press disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={20} /> Sending...
                  </>
                ) : (
                  <>Send Inquiry ✦</>
                )}
              </button>
            </form>

            <p className="text-white/80 text-sm mt-5 font-quicksand">
              Prefer email? Reach us at{" "}
              <a href="mailto:help@universalhousetx.com" className="underline font-bold text-white">
                help@universalhousetx.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
